import React, { forwardRef, isValidElement } from "react";
import { motion } from "framer-motion";
import clsx from "clsx";

export const Avatar = ({
  t, size = 42, bg = "#1B2A4A", col = "#fff", r = "50%", fs, className = "",
}) => (
  <div
    className={clsx(
      "flex items-center justify-center font-sora font-extrabold shrink-0",
      className
    )}
    style={{
      width: size, height: size, borderRadius: r,
      background: bg, color: col,
      fontSize: fs || size * 0.35,
    }}
  >
    {t}
  </div>
);

export const Pill = ({ children, col, color, bg, border, onClick, style = {}, className = "" }) => (
  <span
    onClick={onClick}
    className={clsx(
      "inline-flex items-center px-3 py-1 rounded-full text-[13px] font-bold font-sora whitespace-nowrap",
      "transition-all duration-200",
      onClick && "cursor-pointer hover:scale-105 active:scale-95",
      !onClick && "cursor-default",
      className
    )}
    style={{
      color: color || col,
      background: bg,
      border: border ? `1px solid ${border}` : "none",
      ...style,
    }}
  >
    {children}
  </span>
);

export const ProgressBar = ({ pct, col, h = 7 }) => (
  <div className="w-full rounded-full overflow-hidden bg-black/[.06]" style={{ height: h }}>
    <motion.div
      className="h-full rounded-full"
      style={{ background: col }}
      initial={{ width: 0 }}
      animate={{ width: `${Math.min(100, pct || 0)}%` }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    />
  </div>
);

export const InputField = ({ label, value, onChange, type = "text", placeholder = "", disabled = false, className = "" }) => (
  <div className={clsx("flex flex-col w-full", className)}>
    {label && <label className="input-label">{label}</label>}
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={clsx(
        "input-field",
        disabled && "!bg-navy-50 !text-navy-400 !cursor-not-allowed"
      )}
    />
  </div>
);

export const SelectField = ({ label, value, onChange, options, className = "" }) => (
  <div className={clsx("flex flex-col w-full", className)}>
    {label && <label className="input-label">{label}</label>}
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="input-field cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%239AA1B4%22%20d%3D%22M6%208L1%203h10z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_16px_center] bg-no-repeat pr-10"
    >
      {options?.length ? options.map(o => <option key={o.v} value={o.v}>{o.l}</option>) : <option value="">No options available</option>}
    </select>
  </div>
);

export const TextAreaField = ({ label, value, onChange, placeholder = "", rows = 3, className = "" }) => (
  <div className={clsx("flex flex-col w-full", className)}>
    {label && <label className="input-label">{label}</label>}
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="input-field resize-y"
    />
  </div>
);

const BUTTON_VARIANTS = {
  primary: "bg-navy-800 text-white border-navy-800 hover:bg-navy-700 shadow-sm hover:shadow-md",
  orange:  "gradient-brand text-white border-transparent hover:opacity-90 shadow-glow-brand",
  ghost:   "bg-transparent text-navy-500 border-navy-200 hover:bg-navy-50 hover:border-navy-300",
  green:   "bg-accent-green text-white border-accent-green hover:brightness-110",
  red:     "bg-accent-red text-white border-accent-red hover:brightness-110",
  purple:  "gradient-purple text-white border-transparent hover:opacity-90 shadow-glow",
};

export const Button = forwardRef(({
  children, onClick, variant = "primary",
  small = false, className = "", disabled = false, type = "button",
  style = {},
  ...rest
}, ref) => (
  <button
    ref={ref}
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={clsx(
      "inline-flex items-center justify-center gap-2 rounded-xl font-sora font-bold whitespace-nowrap",
      "border-[1.5px] transition-all duration-200 active:scale-[0.97]",
      small ? "px-4 py-2 text-[13px]" : "px-6 py-3 text-[15px]",
      BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary,
      disabled && "!opacity-50 !cursor-not-allowed !pointer-events-none",
      !disabled && "cursor-pointer",
      className
    )}
    style={style}
    {...rest}
  >
    {children}
  </button>
));
Button.displayName = "Button";

export const Card = ({ children, className = "", hover = false, style = {}, ...rest }) => (
  <div
    className={clsx(
      "bg-white rounded-2xl border border-navy-100 shadow-card overflow-hidden",
      hover && "transition-all duration-300 hover:shadow-card-hover hover:-translate-y-0.5 cursor-pointer",
      className
    )}
    style={style}
    {...rest}
  >
    {children}
  </div>
);

export const CardHeader = ({ title, icon, right, className = "" }) => {
  const IconComponent = icon;
  return (
    <div className={clsx(
      "px-6 py-[18px] border-b border-navy-50 flex items-center justify-between",
      className
    )}>
      <div className="font-sora font-bold text-base text-navy-800 flex items-center gap-2.5">
        {icon && (
          isValidElement(icon)
            ? <span className="text-xl flex items-center">{icon}</span>
            : typeof icon === 'string'
              ? <span className="text-xl">{icon}</span>
              : <span className="text-xl flex items-center"><IconComponent size={20} /></span>
        )}
        {title}
      </div>
      {right}
    </div>
  );
};

export const StatCard = ({ icon, value, label, accent, iconBg, delay = 0 }) => (
  <motion.div
    className="stat-card"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
  >
    <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: accent }} />
    <div
      className="w-13 h-13 rounded-xl flex items-center justify-center text-2xl mb-4"
      style={{ background: iconBg }}
    >
      {icon}
    </div>
    <div className="font-sora font-extrabold text-[32px] text-navy-800 leading-none">
      {value}
    </div>
    <div className="text-sm text-navy-400 mt-1.5">{label}</div>
  </motion.div>
);

export const EmptyState = ({ icon, title, description, action }) => (
  <motion.div
    className="flex flex-col items-center justify-center py-16 px-8 text-center"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
  >
    {icon && <div className="text-5xl mb-4 opacity-40">{icon}</div>}
    <h3 className="font-sora font-bold text-lg text-navy-800 mb-2">{title}</h3>
    {description && <p className="text-navy-400 text-sm max-w-sm">{description}</p>}
    {action && <div className="mt-6">{action}</div>}
  </motion.div>
);

export const PageTransition = ({ children, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export const Pagination = ({ page, totalPages, total, limit, onPageChange, className = "" }) => {
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push("...");
  }
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push("...");
    pages.push(totalPages);
  }

  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div className={clsx("flex items-center justify-between border-t border-navy-50 px-5 py-3", className)}>
      <span className="font-body text-[13px] text-navy-400">
        Showing {from}–{to} of {total}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-navy-100 bg-white text-[13px] font-semibold text-navy-500 transition-all hover:border-brand-400 hover:text-brand-400 disabled:cursor-not-allowed disabled:opacity-40"
        >
          ‹
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`dots-${i}`} className="px-1 text-[13px] text-navy-300">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={clsx(
                "flex h-8 min-w-[32px] cursor-pointer items-center justify-center rounded-lg border text-[13px] font-bold transition-all",
                p === page
                  ? "border-brand-400 bg-brand-400 text-white shadow-sm"
                  : "border-navy-100 bg-white text-navy-600 hover:border-brand-400 hover:text-brand-400"
              )}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-navy-100 bg-white text-[13px] font-semibold text-navy-500 transition-all hover:border-brand-400 hover:text-brand-400 disabled:cursor-not-allowed disabled:opacity-40"
        >
          ›
        </button>
      </div>
    </div>
  );
};

// Theme components
export { default as ThemeSwitcher } from "./ThemeSwitcher";
export { ThemeProvider, useTheme, THEMES } from "../../context/ThemeContext";
